import { z } from 'zod';
import { request } from './api-client';

/**
 * What every external dependency is configured with, and whether it works (spec § 4.4).
 *
 * There is deliberately no function here that SETS a credential, and there is no endpoint behind one.
 * Secrets arrive from the environment — `.env` locally, Key Vault when deployed — because a secret
 * typed into a browser lands in a request log, the browser's form history and possibly a password
 * manager (`security.md` § 1).
 */

export const IntegrationSummarySchema = z.object({
  key: z.string(),
  label: z.string(),
  /** What breaks if this is wrong. Carried on the wire so the reason lives with the check. */
  consequence: z.string(),
  /** Which implementation is live, where there is a choice. Null where there is only one. */
  driver: z.string().nullable(),
  /** A credential is present. NOT that it works — that needs a test. */
  isConfigured: z.boolean(),
  /** The last four characters, or null. Answers "is it the right key" without revealing it. */
  credentialFingerprint: z.string().nullable(),
  /** Whether pressing Test spends money. Shown before it is pressed, not after. */
  testCostsMoney: z.boolean(),
});

export type IntegrationSummary = z.infer<typeof IntegrationSummarySchema>;

export const IntegrationTestResultSchema = z.object({
  key: z.string(),
  /** False when nothing was configured, so nothing was called. Not the same as a failure. */
  wasRun: z.boolean(),
  isHealthy: z.boolean(),
  detail: z.string(),
  /** What the check learned beyond pass or fail — scopes, a version, a model. */
  extra: z.string().nullable(),
  cost: z.string().nullable(),
});

export type IntegrationTestResult = z.infer<typeof IntegrationTestResultSchema>;

export const IntegrationTestSweepSchema = z.object({
  results: z.array(IntegrationTestResultSchema),
  /** Keys not run because they are billable. Named rather than silently absent. */
  skipped: z.array(z.string()),
});

export type IntegrationTestSweep = z.infer<typeof IntegrationTestSweepSchema>;

export function listIntegrations() {
  return request('/api/v1/settings/integrations', z.array(IntegrationSummarySchema));
}

export function testIntegration(key: string) {
  return request(
    `/api/v1/settings/integrations/${encodeURIComponent(key)}/test`,
    IntegrationTestResultSchema,
    { method: 'POST' },
  );
}

export function testFreeIntegrations() {
  return request('/api/v1/settings/integrations/test', IntegrationTestSweepSchema, {
    method: 'POST',
  });
}
